import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  roomChannelNames,
  type RoomConnectionState,
  type RoomPresenceMember,
  type RoomTypingPayload,
} from "@/lib/realtime/roomChannels";

interface UseRoomPresenceOptions {
  roomId: string | undefined;
  enabled: boolean;
  displayName?: string | null;
  email?: string | null;
}

interface TypingState {
  user_id: string;
  name: string;
  expiresAt: number;
}

const TYPING_TTL_MS = 4000;
const TYPING_STOP_DEBOUNCE_MS = 1500;

/**
 * Subscribes to the room's presence channel and tracks the current user.
 * Returns the list of other online members and active typers, plus a
 * debounced helper to broadcast typing status.
 */
export function useRoomPresence({
  roomId,
  enabled,
  displayName,
  email,
}: UseRoomPresenceOptions) {
  const { user } = useAuth();
  const [onlineMembers, setOnlineMembers] = useState<RoomPresenceMember[]>([]);
  const [typingUsers, setTypingUsers] = useState<TypingState[]>([]);
  const [connectionState, setConnectionState] =
    useState<RoomConnectionState>("disconnected");

  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const stopTypingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingSentRef = useRef<boolean>(false);

  // Self identity (stable per render)
  const self = useMemo<RoomPresenceMember | null>(() => {
    if (!user?.id) return null;
    return {
      user_id: user.id,
      name: displayName || email || user.email || "User",
      email: email || user.email || null,
      online_at: new Date().toISOString(),
    };
  }, [user?.id, user?.email, displayName, email]);

  useEffect(() => {
    if (!enabled || !roomId || !self) return;

    setConnectionState("connecting");

    const channel = supabase.channel(roomChannelNames.presence(roomId), {
      config: {
        presence: { key: self.user_id },
        broadcast: { self: false },
      },
    });
    channelRef.current = channel;

    channel.on("presence", { event: "sync" }, () => {
      const state = channel.presenceState() as Record<
        string,
        RoomPresenceMember[]
      >;
      const flat: RoomPresenceMember[] = [];
      for (const key of Object.keys(state)) {
        const first = state[key]?.[0];
        if (first && first.user_id !== self.user_id) flat.push(first);
      }
      // Dedupe by user_id (multiple tabs)
      const seen = new Set<string>();
      const deduped = flat.filter((m) => {
        if (seen.has(m.user_id)) return false;
        seen.add(m.user_id);
        return true;
      });
      setOnlineMembers(deduped);
    });

    channel.on("broadcast", { event: "typing" }, ({ payload }) => {
      const evt = payload as RoomTypingPayload;
      if (!evt || evt.user_id === self.user_id) return;
      setTypingUsers((prev) => {
        const next = prev.filter((t) => t.user_id !== evt.user_id);
        if (evt.typing) {
          next.push({
            user_id: evt.user_id,
            name: evt.name,
            expiresAt: Date.now() + TYPING_TTL_MS,
          });
        }
        return next;
      });
    });

    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        setConnectionState("connected");
        try {
          await channel.track(self);
        } catch (e) {
          // ignore
        }
      } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
        setConnectionState("reconnecting");
      } else if (status === "CLOSED") {
        setConnectionState("disconnected");
      }
    });

    return () => {
      if (stopTypingTimer.current) clearTimeout(stopTypingTimer.current);
      try {
        channel.untrack();
      } catch {
        // ignore
      }
      supabase.removeChannel(channel);
      channelRef.current = null;
      setOnlineMembers([]);
      setTypingUsers([]);
      setConnectionState("disconnected");
    };
  }, [enabled, roomId, self?.user_id, self?.name, self?.email]);

  // Expire stale typing entries
  useEffect(() => {
    if (typingUsers.length === 0) return;
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingUsers((prev) => prev.filter((t) => t.expiresAt > now));
    }, 1000);
    return () => clearInterval(interval);
  }, [typingUsers.length]);

  const broadcastTyping = useCallback(
    (isTyping: boolean) => {
      const channel = channelRef.current;
      if (!channel || !self) return;

      if (isTyping) {
        if (!lastTypingSentRef.current) {
          lastTypingSentRef.current = true;
          channel.send({
            type: "broadcast",
            event: "typing",
            payload: { user_id: self.user_id, name: self.name, typing: true },
          });
        }
        if (stopTypingTimer.current) clearTimeout(stopTypingTimer.current);
        stopTypingTimer.current = setTimeout(() => {
          lastTypingSentRef.current = false;
          channel.send({
            type: "broadcast",
            event: "typing",
            payload: {
              user_id: self.user_id,
              name: self.name,
              typing: false,
            },
          });
        }, TYPING_STOP_DEBOUNCE_MS);
      } else {
        if (stopTypingTimer.current) clearTimeout(stopTypingTimer.current);
        if (lastTypingSentRef.current) {
          lastTypingSentRef.current = false;
          channel.send({
            type: "broadcast",
            event: "typing",
            payload: {
              user_id: self.user_id,
              name: self.name,
              typing: false,
            },
          });
        }
      }
    },
    [self?.user_id, self?.name],
  );

  return {
    onlineMembers,
    typingUsers,
    broadcastTyping,
    connectionState,
  };
}
